// React Compiler opt-out: legacy practice-view container reading mandark query
// resources and legacy stores throughout render; convert reads before compiling.
'use no memo';

import {Button} from '@albert-io/atomic';
import {queryBuilderPropType} from '@albert-io/json-api-framework/request/builder';
import PracticeCalculator from 'client/components/CalculatorChrome/PracticeCalculator.react';
import {resolveContentCalculatorTier} from 'client/components/CalculatorChrome/calculatorTier';
import {CALCULATOR_DOCK_MARGIN_CLASS} from 'client/components/CalculatorChrome/calculatorWindow.store';
import {useContentDiscoveryStore} from 'client/components/ContentDiscovery/store/ContentDiscoveryStore.react';
import {getQuestionSetGuideLevelIds} from 'client/components/ContentDiscovery/ManageRestrictionsModal';
import {destroyQuestionStoresForQuestionSets} from 'client/components/QuestionTypes/questionTypes.utils';
import {getQuestionEditPath} from 'client/Dennis/Content/Queue/shared';
import {callAction} from 'client/framework';
import globalNavigationActions from 'client/GlobalNavigation/GlobalNavigationActions';
import {history} from 'client/history';
import {showGlobalLoginModal} from 'client/LogIn/utils';
import sessionStore from 'client/Session/SessionStore';
import {addSearchParamsToLink, isSearchMode} from 'components/AdvancedSearch/AdvancedSearch.utils';
import BlockedQuestionsModal from 'components/PracticeView/BlockedQuestionsModal/BlockedQuestionsModal.react';
import FoldersModal from 'components/PracticeView/FoldersModal/FoldersModal.react';
import HeadingActions from 'components/PracticeView/HeadingActions/HeadingActions.react';
import {makeGuessResultManager} from 'components/PracticeView/helpers/guessResultManager';
import makeTimeElapsedManager from 'components/PracticeView/helpers/makeTimeElapsedManager';
import PaginatedResultsManager from 'components/PracticeView/helpers/paginatedResultsManager';
import {
  getNextQuestionState,
  getPreviousQuestionState
} from 'components/PracticeView/helpers/questionNavigation.utils';
import LoadingView from 'components/PracticeView/LoadingView/LoadingView.react';
import {MobilePracticeViewProvider} from 'components/PracticeView/MobilePracticeViewProvider.context';
import {
  discardQuestionAnnotations,
  useAnnotationModeReset
} from 'components/PracticeView/Annotations/store/AnnotationModeStore';

import {useIsSubjectPracticeAnnotationAvailable} from './useIsSubjectPracticeAnnotationAvailable';
import PracticeView from 'components/PracticeView/PracticeView.react';
import {hasActiveFilter} from 'components/PracticeView/PracticeViewToolbar/Filters/filters.utils';
import {PRACTICE_VIEW_MIN_WIDTH} from 'components/PracticeView/practiceViewLayout';
import {
  canAddAllToFolder,
  canRevealSolution,
  canSaveQuestionSetToFolder,
  hasEditPermissions,
  hasMoreQuestionInfo,
  hasPreviousAttempts,
  makePreviousAttemptsQuery,
  showPublicViewForSubject
} from 'components/PracticeView/practiceView.utils';
import {
  EditQuestionLink,
  MoreInfo,
  Next,
  QuestionActionsWrapper,
  SubmitAnswer,
  ToggleViewSolution,
  TryAgain
} from 'components/PracticeView/QuestionActions';
import clearGuess from 'components/PracticeView/QuestionActions/clearGuess';
import QuestionsListHeader from 'components/PracticeView/QuestionsListHeader/QuestionsListHeader.react';
import QuestionsListPaginator from 'components/PracticeView/QuestionsListPaginator/QuestionsListPaginator.react';
import TitleBar from 'components/PracticeView/TitleBar/TitleBar.react';
import UpgradeSubjectModal from 'components/PracticeView/UpgradeSubjectModal/UpgradeSubjectModal';
import {TopicSummary} from 'components/TopicSummary/TopicSummary.react';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import {List, OrderedSet} from 'immutable';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import {pluralize, upperFirst} from 'lib/stringUtils';
import {userLicensedSubjectsQuery} from 'lib/UserAccessUtil';
import PropTypes from 'prop-types';
import React from 'react';
import {Link} from 'react-router';
import {Map} from 'immutable';
import track from 'react-tracking';
import {getModelForResourceType} from 'resources/modelRegistry';

import studyGuideUtils from 'client/StudyGuide/utils';

import {
  ARCHIVED_DISABLED_TOOLTIP,
  isQuestionArchived
} from 'client/components/ArchivedQuestions/archivedQuestions.utils';

import PublicQuestions from 'components/PublicQuestions';

import NoQuestionSetsFoundSubjectPracticeView from './NoQuestionSetsFoundSubjectPracticeView/NoQuestionSetsFoundSubjectPracticeView.react';
import RetailAssessmentModal from './RetailAssessmentModal/RetailAssessmentModal.react';
import {
  getQuestionGuideLevels,
  getQuestionSetsData,
  getSingleQuestionSetQuery,
  getSubjectBySlugQuery,
  getSubjectGuideLevelBySlug,
  queryTypes
} from './subjectPracticeView.queries';
import {
  getActiveQuestionPath,
  getActiveSetAndQuestion,
  getPathToQuestionSetAndQuestion,
  getRedirectPath,
  getTopicSummaryPath,
  hasTopicSummary
} from './subjectPracticeView.utils';
import UnsubmittedResponseGuard from './UnsubmittedResponseGuard/UnsubmittedResponseGuard';
import SubjectPracticeViewHelmet from './SubjectPracticeViewHelmet.react';
import UnavailableQuestionSubjectPracticeView from './UnavailableQuestionSubjectPracticeView/UnavailableQuestionSubjectPracticeView';
import SubjectPracticeViewToolbar from './SubjectPracticeViewToolbar/SubjectPracticeViewToolbar.react';

@track({feature: 'Subject Practice View'})
class BootstrappedSubjectPracticeView extends React.Component {
  static propTypes = {
    supportsAnnotations: PropTypes.bool,
    guideLevel: PropTypes.instanceOf(getModelForResourceType('guide_level_v2')),
    subject: PropTypes.instanceOf(getModelForResourceType('subject_v2')),
    questionSets: PropTypes.instanceOf(List),
    questionSetsQuery: queryBuilderPropType,
    questionCount: PropTypes.number,
    questionSetsCount: PropTypes.number,
    route: PropTypes.object,
    params: PropTypes.object,
    router: PropTypes.object,
    isLoading: PropTypes.bool,
    location: PropTypes.object,
    prevLocation: PropTypes.string,
    retailStudentInAssessment: PropTypes.bool,
    partialCreditEnabled: PropTypes.bool,
    guesses: PropTypes.instanceOf(List)
  };

  constructor(props) {
    super(props);
    const {
      activeQuestionSet,
      activeQuestion,
      guessResults,
      selectedQuestionSets,
      activeGuess,
      paginatedQuestionsListManager
    } = this.getBootstrapValues();
    this.state = {
      activeQuestionSet,
      activeQuestion,
      guessResults,
      selectedQuestionSets,
      activeGuess,
      openedModalFromHeading: false,
      showFolderModal: false,
      isSelectAllQuestionSetsMode: false,
      showAttemptIndicators: true,
      showRetailAssessmentModal: props.retailStudentInAssessment,
      isRedirectToNewABFlagEnabled: false
    };
    this.paginatedQuestionsListManager = paginatedQuestionsListManager;
    this.timeElapsedManager = makeTimeElapsedManager(activeQuestion);
    this.scrollToQuestionById = React.createRef();
    this.unsubmittedResponseGuard = React.createRef();
  }

  async componentDidMount() {
    const {route, router, params, location, guideLevel, questionSets} = this.props;
    const {activeQuestion, activeQuestionSet} = this.state;
    const redirectPath = getRedirectPath({
      route,
      params,
      location,
      guideLevel,
      questionSets,
      activeQuestion,
      activeQuestionSet
    });
    if (redirectPath) {
      router.replace(redirectPath);
    }
    /**
     * This button should be handling its show/hide state. Update that.
     */
    callAction(globalNavigationActions.SET_SHOULD_SHOW_UPGRADE_BUTTON, true);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevProps.questionSetsQuery.equals(this.props.questionSetsQuery)) {
      const {
        activeQuestionSet,
        activeQuestion,
        guessResults,
        selectedQuestionSets,
        activeGuess,
        paginatedQuestionsListManager
      } = this.getBootstrapValues();
      this.setState({
        activeQuestionSet,
        activeQuestion,
        guessResults,
        selectedQuestionSets,
        activeGuess
      });
      this.paginatedQuestionsListManager = paginatedQuestionsListManager;
    }
    /**
     * @todo: QuestionStores should be the only ones responsible for keeping track of their active guess.
     * That's not the case right now, as we're also keeping track of the active guess in component state.
     * Once we consolidate the active guess to once place, we can remove this.
     */
    if (
      prevState.activeQuestion &&
      (!this.state.activeQuestion ||
        prevState.activeQuestion.getId() !== this.state.activeQuestion.getId())
    ) {
      clearGuess(prevState.activeQuestion.getStoreName());
    }

    this.timeElapsedManager.handleQuestionChange(this.state.activeQuestion);
  }

  componentWillUnmount() {
    /**
     * This button should be handling its show/hide state. Update that.
     */
    callAction(globalNavigationActions.SET_SHOULD_SHOW_UPGRADE_BUTTON, false);
    destroyQuestionStoresForQuestionSets(this.props.questionSets);
  }

  getBootstrapValues() {
    const {guesses, partialCreditEnabled, questionSetsQuery} = this.props;

    const activeSetAndQuestion = getActiveSetAndQuestion(this.props);
    const initialGuesses = guesses.reduce((accumulator, guess) => {
      if (guess && typeof guess.get('question')?.getId === 'function') {
        return accumulator.set(
          guess.get('question').getId(),
          Map({
            guess,
            result: guess.getCorrectness(partialCreditEnabled)
          })
        );
      } else {
        return accumulator;
      }
    }, Map());
    return {
      activeQuestionSet: activeSetAndQuestion.activeQuestionSet,
      activeQuestion: activeSetAndQuestion.activeQuestion,
      guessResults: makeGuessResultManager(initialGuesses),
      selectedQuestionSets: OrderedSet(),
      activeGuess: null,
      paginatedQuestionsListManager: new PaginatedResultsManager({
        initialQuery: questionSetsQuery,
        onUpdate: (incomingQuestionSets) => {
          if (!incomingQuestionSets) {
            this.forceUpdate();
          }
        }
      })
    };
  }

  toggleRetailAssessmentModal = () => {
    this.setState((state) => ({
      showRetailAssessmentModal: !state.showRetailAssessmentModal
    }));
  };

  toggleFoldersModal = () => {
    if (this.state.showFolderModal) {
      this.setState({
        showFolderModal: false,
        openedModalFromHeading: false
      });
    } else {
      this.setState({
        showFolderModal: true
      });
    }
  };

  toggleFoldersModalFromHeading = () => {
    this.setState({
      openedModalFromHeading: true
    });
    this.toggleFoldersModal();
  };

  makeQuestionSetOnChangeHandler = (questionSet) => {
    return () => {
      const questionSetId = questionSet.getId();
      this.setState((state) => {
        const newSelectedSets = state.selectedQuestionSets.includes(questionSetId)
          ? state.selectedQuestionSets.delete(questionSetId)
          : state.selectedQuestionSets.add(questionSetId);
        const isDeselectingLastSelectAllSet =
          state.isSelectAllQuestionSetsMode &&
          newSelectedSets.size === this.props.questionSetsCount;

        return isDeselectingLastSelectAllSet
          ? {
              selectedQuestionSets: OrderedSet(),
              isSelectAllQuestionSetsMode: false
            }
          : {
              selectedQuestionSets: newSelectedSets
            };
      });
    };
  };

  handleGuessSubmission = (guess) => {
    const question = guess.getQuestion();
    const questionId = question.getId();
    const questionSetId = question.getQuestionSet().getId();

    discardQuestionAnnotations(questionId);
    makePreviousAttemptsQuery(questionId).invalidateInterest();

    this.paginatedQuestionsListManager.updateQuestionForQuestionSetId({
      questionSetId,
      question
    });

    this.setState((state) => ({
      activeQuestion: question,
      activeGuess: guess,
      guessResults: state.guessResults.updateGuess(
        questionId,
        guess,
        false,
        this.props.partialCreditEnabled
      )
    }));
  };

  resetGuess = () => {
    this.setState({activeGuess: null});
    this.timeElapsedManager.resetTimeForActiveQuestion();
  };

  setActiveGuess = (guess) => {
    this.setState({activeGuess: guess});
  };

  getStageComponent() {
    if (this.props.route.isTopicSummary) {
      return (
        <TopicSummary
          guideLevel={this.props.guideLevel}
          practiceButtonLink={getActiveQuestionPath(this.props)}
        />
      );
    }
    return null;
  }

  makeNavigateProps(upcomingSetAndQuestion) {
    if (!upcomingSetAndQuestion) {
      return {disabled: true};
    }
    const {questionSet, question} = upcomingSetAndQuestion;
    return {
      disabled: false,
      handleNavigation: async () => {
        if (!(await this.confirmLeavingActiveQuestion(question))) {
          return;
        }
        this.scrollToQuestionById.current(questionSet.getId());
        this.navigateToQuestion(questionSet, question);
      }
    };
  }

  makeNavigateBackProps = () => {
    const questionSets = this.paginatedQuestionsListManager.getResults();
    if (questionSets.isEmpty()) {
      return null;
    }
    const previousSetAndQuestion = getPreviousQuestionState({
      questionSets,
      activeSet: this.state.activeQuestionSet,
      activeQuestion: this.state.activeQuestion
    });
    return this.makeNavigateProps(previousSetAndQuestion);
  };

  makeNavigateNextProps = () => {
    const questionSets = this.paginatedQuestionsListManager.getResults();
    if (questionSets.isEmpty()) {
      return null;
    }
    const nextSetAndQuestion = getNextQuestionState({
      questionSets,
      activeSet: this.state.activeQuestionSet,
      activeQuestion: this.state.activeQuestion
    });
    return this.makeNavigateProps(nextSetAndQuestion);
  };

  /**
   * Resolves false when the student chooses to stay on an unsubmitted response instead.
   */
  async confirmLeavingActiveQuestion(question) {
    const guard = this.unsubmittedResponseGuard.current;
    const isLeavingActiveQuestion = question?.getId() !== this.state.activeQuestion?.getId();
    if (!isLeavingActiveQuestion || !guard?.hasUnsubmittedResponse()) {
      return true;
    }
    return guard.confirmLeave();
  }

  navigateToQuestion(questionSet, question) {
    return new Promise((res) => {
      const path = getPathToQuestionSetAndQuestion({
        subjectSlug: this.props.params.subjectSlug,
        guideLevelSlug: this.props.params.guideLevelSlug,
        questionSet,
        question
      });
      // A locked question has its slug fields redacted (#19056), so there is no URL to push;
      // the selection still has to advance or navigation dead-ends on that question.
      if (path) {
        this.unsubmittedResponseGuard.current?.allowNavigationTo(path);
        this.props.router.push(path);
      }
      this.setState(
        {
          activeQuestionSet: questionSet,
          activeQuestion: question,
          /**
           * @todo: Figure out if we wanna load the previous guess when navigating to questions
           */
          activeGuess: null
        },
        () => {
          res();
        }
      );
    });
  }

  async navigateAndScrollToPage(pageNum) {
    const firstSetOnPage = this.paginatedQuestionsListManager
      .getQuestionSetsInPage(pageNum)
      .first();
    const firstQuestion = firstSetOnPage.getQuestions().first();
    if (!(await this.confirmLeavingActiveQuestion(firstQuestion))) {
      return;
    }
    this.navigateToQuestion(firstSetOnPage, firstQuestion).then(() =>
      this.scrollToQuestionById.current(firstSetOnPage.getId())
    );
  }

  handlePaginatorPreviousClick = async () => {
    const previousPage =
      this.paginatedQuestionsListManager.getPageForId(this.state.activeQuestionSet.getId()) - 1;
    if (!this.paginatedQuestionsListManager.hasFetchedPage(previousPage)) {
      await this.paginatedQuestionsListManager.fetchPreviousPage();
    }
    this.navigateAndScrollToPage(previousPage);
  };

  handlePaginatorNextClick = async () => {
    const nextPage =
      this.paginatedQuestionsListManager.getPageForId(this.state.activeQuestionSet.getId()) + 1;
    if (!this.paginatedQuestionsListManager.hasFetchedPage(nextPage)) {
      await this.paginatedQuestionsListManager.fetchNextPage();
    }
    this.navigateAndScrollToPage(nextPage);
  };

  handleSelectAssign = async () => {
    const {
      setLegacyQuestionsIds,
      setLegacyItemId,
      setLegacyItemType,
      setLegacyQuestionsIdsExcluded
    } = useContentDiscoveryStore.getState().draft;
    const {isSelectAllQuestionSetsMode, selectedQuestionSets} = this.state;
    const guideLevelId = this.props.guideLevel?.getId();
    const selectedQuestionSetIds = selectedQuestionSets.toArray();
    const allQuestionSetsInMemory = this.paginatedQuestionsListManager.getResults();

    if (!isSelectAllQuestionSetsMode && !!selectedQuestionSetIds.length) {
      setLegacyQuestionsIds(selectedQuestionSetIds);
    } else if (allQuestionSetsInMemory.size === this.props.questionSetsCount) {
      setLegacyQuestionsIds(
        allQuestionSetsInMemory
          .filter((questionSet) => !selectedQuestionSetIds.includes(questionSet.getId()))
          .map((qs) => qs.getId())
          .toArray()
      );
    } else {
      setLegacyQuestionsIdsExcluded(selectedQuestionSetIds);
    }

    setLegacyItemId(guideLevelId);
    setLegacyItemType('guide');
    history.pushState(null, `/assignment-builder/create-assignment/settings`);
  };

  clearQuestionSetSelections = () => {
    this.setState({
      selectedQuestionSets: OrderedSet(),
      isSelectAllQuestionSetsMode: false
    });
  };

  getHeadingActions() {
    const {activeQuestion, activeQuestionSet} = this.state;
    const {subject} = this.props;
    if (!activeQuestion) {
      return null;
    }

    const isQuestionBlocked = activeQuestion.getMeta().isBlockedAssignmentQuestion();

    const actions = [];
    if (!isQuestionBlocked && hasPreviousAttempts(activeQuestion)) {
      actions.push(
        <HeadingActions.PreviousAttempts
          key='PreviousAttempts'
          onGuessClick={this.setActiveGuess}
        />
      );
    }
    if (hasEditPermissions(activeQuestion)) {
      const subjectId = this.paginatedQuestionsListManager
        .getResults()
        .first()
        .getSubject()
        .getId();
      const questionSetId = activeQuestionSet.getId();
      actions.push(
        <HeadingActions.EditQuestionItem
          key='EditQuestionItem'
          editPath={getQuestionEditPath({
            questionSetId: activeQuestionSet.getId(),
            questionId: activeQuestion.getId(),
            customSearch: {
              status: 'published',
              subject: subjectId,
              page: questionSetId,
              fallback_page: 1
            }
          })}
        />
      );
    }
    if (!isQuestionBlocked && canSaveQuestionSetToFolder(subject)) {
      actions.push(
        <HeadingActions.SaveToItem key='SaveToItem' onClick={this.toggleFoldersModalFromHeading} />
      );
    }

    return !!actions.length && <HeadingActions>{actions}</HeadingActions>;
  }

  getTitleBarActions() {
    const actions = [];

    if (!this.props.route.isTopicSummary && hasTopicSummary(this.props)) {
      const guideLevelType = this.props.guideLevel.getGuideLevelType();
      actions.push(
        <TitleBar.DropdownItem
          key='viewSubjectOverview'
          as={Link}
          to={getTopicSummaryPath(this.props)}
          preventDefaultClickHandler={false}
        >
          View {upperFirst(guideLevelType)} Overview
        </TitleBar.DropdownItem>
      );
    }

    return !!actions.length && <TitleBar.Dropdown>{actions}</TitleBar.Dropdown>;
  }

  getFooterActions() {
    const {subject} = this.props;
    const {activeQuestion, activeGuess, activeQuestionSet} = this.state;
    const isQuestionBlocked = activeQuestion.getMeta().isBlockedAssignmentQuestion();

    const actions = [];
    let key = 0;
    if (isQuestionBlocked) {
      actions.push(
        <Button as={Link} to='/classes'>
          Go to classes
        </Button>
      );
    }
    if (hasMoreQuestionInfo()) {
      actions.push(
        <MoreInfo
          key={(key += 1)}
          questionSetId={activeQuestionSet.getId()}
          userId={masqueradeStore.getUserIdByMasqueradeState()}
        />
      );
    }
    if (
      canRevealSolution({
        question: activeQuestion,
        questionSet: activeQuestionSet,
        subject
      })
    ) {
      actions.push(
        <ToggleViewSolution
          handleToggleSolution={activeGuess ? this.resetGuess : this.setActiveGuess}
          isViewingSolution={!!activeGuess}
          key={(key += 1)}
          question={activeQuestion}
          questionStoreName={activeQuestion.getStoreName()}
        />
      );
    }
    if (activeGuess || isQuestionBlocked) {
      const {handleNavigation} = this.makeNavigateNextProps();
      if (handleNavigation) {
        actions.push(<Next key={(key += 1)} onClick={handleNavigation} />);
      }
    }
    if (activeGuess) {
      actions.push(
        <TryAgain
          handleResetGuess={this.resetGuess}
          key={(key += 1)}
          questionStoreName={activeQuestion.getStoreName()}
        />
      );
    }
    if (!activeGuess && !isQuestionBlocked && !subject.isAdaptive()) {
      actions.push(
        <SubmitAnswer
          hasAccess
          getTimeElapsed={() => this.timeElapsedManager.getTimeElapsed(activeQuestion)}
          key={(key += 1)}
          onSuccess={this.handleGuessSubmission}
          question={activeQuestion}
          subject={subject}
        />
      );
    }
    if (hasEditPermissions(activeQuestion)) {
      actions.push(
        <EditQuestionLink
          key={(key += 1)}
          question={activeQuestion}
          questionSet={activeQuestionSet}
        />
      );
    }
    return (
      <QuestionActionsWrapper questionId={activeQuestion.getId()}>{actions}</QuestionActionsWrapper>
    );
  }

  toggleSelectAllQuestionSets = (e) => {
    this.setState({
      isSelectAllQuestionSetsMode: e.target.checked,
      selectedQuestionSets: OrderedSet()
    });
  };

  isQuestionSetSelected = (questionSet) => {
    const {selectedQuestionSets, isSelectAllQuestionSetsMode} = this.state;
    const isIncluded = selectedQuestionSets.includes(questionSet.getId());
    return isSelectAllQuestionSetsMode ? !isIncluded : isIncluded;
  };

  getSelectedQuestionCount() {
    const {selectedQuestionSets, isSelectAllQuestionSetsMode} = this.state;
    const selectedCount = selectedQuestionSets.reduce((acc, id) => {
      return acc + this.paginatedQuestionsListManager.getQuestionSetById(id).getQuestions().size;
    }, 0);
    return isSelectAllQuestionSetsMode ? this.props.questionCount - selectedCount : selectedCount;
  }

  render() {
    const {
      questionSetsQuery,
      guideLevel,
      params,
      isLoading,
      subject,
      route,
      prevLocation,
      retailStudentInAssessment,
      partialCreditEnabled
    } = this.props;
    const {
      activeQuestion,
      activeQuestionSet,
      activeGuess,
      rearrangeable,
      showAttemptIndicators,
      strikethroughQuestionInfo,
      isSelectAllQuestionSetsMode
    } = this.state;
    const questionSets = this.paginatedQuestionsListManager.getResults();
    const {subjectSlug, guideLevelSlug} = params;
    const isRestrictionAdmin =
      sessionStore.isSchoolAdministrator() &&
      guideLevel?.getGuide()?.getGuideType() === 'assessment';
    const restrictionGuideLevelName = isRestrictionAdmin ? guideLevel.getName() : null;
    const restrictionGuideLevelHref = isRestrictionAdmin ? `/${subjectSlug}/assessments` : null;
    const restrictionGuideLevelId = isRestrictionAdmin ? guideLevel.getId() : null;
    const activeSubject = activeQuestionSet ? activeQuestionSet.getSubject() : subject;
    const subjectLink = activeSubject.isAdaptive()
      ? `/adaptive-subjects/${subjectSlug}`
      : `/${subjectSlug}`;
    // Direct question links (/learn/question/…) load exactly one question; when it is
    // archived, selection and assignment are unavailable (#14157).
    const isArchivedQuestionView =
      route.queryType === queryTypes.singleQuestion && isQuestionArchived(activeQuestion);
    const toolbar = !activeSubject.isAdaptive() ? (
      <SubjectPracticeViewToolbar
        location={this.props.location}
        questionSetsQuery={this.props.questionSetsQuery}
        isSelectAllQuestionSetsMode={this.state.isSelectAllQuestionSetsMode}
        activeQuestionSet={this.state.activeQuestionSet}
        subject={this.props.subject}
        guideLevel={guideLevel}
        selectedQuestionCount={this.getSelectedQuestionCount()}
        clearQuestionSetSelections={this.clearQuestionSetSelections}
        handleSelectAssign={this.handleSelectAssign}
        toggleFoldersModal={this.toggleFoldersModal}
        canAssignGuide={canAddAllToFolder(activeSubject, guideLevel)}
        isEmptyPracticeView={!activeQuestion}
        toggleRetailAssessmentModal={retailStudentInAssessment && this.toggleRetailAssessmentModal}
        isLoading={isLoading}
        isArchivedQuestionView={isArchivedQuestionView}
      />
    ) : null;
    if (questionSets.isEmpty()) {
      const isUnavailableQuestionView =
        !isLoading &&
        route.queryType === queryTypes.singleQuestion &&
        !hasActiveFilter() &&
        !isSearchMode();
      if (isUnavailableQuestionView) {
        return (
          <UnavailableQuestionSubjectPracticeView subject={subject} subjectSlug={subjectSlug} />
        );
      }
      return (
        <NoQuestionSetsFoundSubjectPracticeView
          subject={subject}
          guideLevel={guideLevel}
          subjectSlug={subjectSlug}
          toolbar={toolbar}
        />
      );
    }

    const totalQuestions = questionSetsQuery
      .getResourceMetadata()
      .getIn(['page', 'total_entries'], 0);

    const navigateBackProps = this.makeNavigateBackProps();
    const navigateNextProps = this.makeNavigateNextProps();
    const curriculumAreaSlug = subject.getCurriculumAreaSlug();
    const backgroundColor = `var(--color-background-domain-${curriculumAreaSlug})`;
    const selectedQuestionCount = this.getSelectedQuestionCount();

    // Smart back button: if FRQ guide and no explicit back state, route to FRQ tab
    const isFrqGuide = guideLevel?.getGuide().getGuideType() === 'free_response';
    const defaultBackPath = isFrqGuide
      ? `/${subjectSlug}/free-response`
      : addSearchParamsToLink(subjectLink);
    const backPath = prevLocation || defaultBackPath;

    const questionListProps = !activeSubject.isAdaptive()
      ? {
          onScrollLastQuestionIntoView: () => {
            if (!this.paginatedQuestionsListManager.isRequestInFlight) {
              this.paginatedQuestionsListManager.fetchNextPage();
            }
          },
          scrollToQuestionById: this.scrollToQuestionById,
          header: (
            <QuestionsListHeader
              checkboxProps={
                canAddAllToFolder(activeSubject, guideLevel)
                  ? {
                      checked:
                        isSelectAllQuestionSetsMode || selectedQuestionCount === totalQuestions,
                      onChange: this.toggleSelectAllQuestionSets,
                      indeterminate:
                        selectedQuestionCount > 0 && selectedQuestionCount !== totalQuestions,
                      disabled: isArchivedQuestionView
                    }
                  : null
              }
              checkboxDisabledTooltip={
                isArchivedQuestionView ? ARCHIVED_DISABLED_TOOLTIP : undefined
              }
              heading={
                totalQuestions !== null
                  ? `${totalQuestions} ${pluralize('question', totalQuestions)}`
                  : null
              }
              selectedQuestionCount={this.getSelectedQuestionCount()}
            />
          ),
          footer: (() => {
            const currentPage = this.paginatedQuestionsListManager.getPageForId(
              activeQuestionSet.getId()
            );
            return (
              <div>
                <QuestionsListPaginator
                  currentPage={currentPage}
                  isRequestInFlight={this.paginatedQuestionsListManager.isRequestInFlight}
                  totalPages={this.paginatedQuestionsListManager.totalPages}
                  isPreviousDisabled={
                    this.paginatedQuestionsListManager.isRequestInFlight || currentPage === 1
                  }
                  handlePreviousPage={this.handlePaginatorPreviousClick}
                  isNextDisabled={
                    this.paginatedQuestionsListManager.isRequestInFlight ||
                    currentPage === this.paginatedQuestionsListManager.totalPages
                  }
                  handleNextPage={this.handlePaginatorNextClick}
                />
              </div>
            );
          })()
        }
      : null;

    // Reserve the gutter only where the toolbar carrying the toggle renders.
    const canAnnotate = Boolean(
      this.props.supportsAnnotations && toolbar && !this.props.route.isTopicSummary
    );
    const calculatorTier =
      activeQuestion &&
      !this.props.route.isTopicSummary &&
      !activeQuestion.getMeta().isBlockedAssignmentQuestion()
        ? resolveContentCalculatorTier({
            questionTier: activeQuestion.getCalculatorType(),
            contentTier: activeQuestion.getMeta().getEffectiveCalculatorType()
          })
        : 'none';

    return (
      <MobilePracticeViewProvider>
        <SubjectPracticeViewHelmet
          route={route}
          subject={activeSubject}
          guideLevel={guideLevel}
          activeQuestion={activeQuestion}
          activeQuestionSet={activeQuestionSet}
        />
        <PracticeView
          supportsAnnotations={canAnnotate}
          activeQuestion={activeQuestion}
          activeQuestionSet={activeQuestionSet}
          activeQuestionStore={activeQuestion && activeQuestion.getStore()}
          activeGuess={activeGuess}
          activeSubject={activeSubject}
          questionSets={questionSets}
          isLoading={isLoading}
          toolbar={!this.props.route.isTopicSummary && toolbar}
          titleBar={
            <TitleBar
              backgroundColor={backgroundColor}
              title={`${isSearchMode() ? 'Search Results in ' : ''}${
                guideLevel ? guideLevel.getName() : activeSubject.getName()
              }`}
              imgHref={activeSubject.getLogoLocation().href}
              backButton={<TitleBar.BackButton as={Link} label='Back To Guide' to={backPath} />}
              dropdown={this.getTitleBarActions()}
            />
          }
          backButtonProps={{
            as: Link,
            label: 'Back To Guide',
            to: subjectLink
          }}
          questionsListProps={questionListProps}
          questionSetProps={{
            selectable: (questionSet) => canSaveQuestionSetToFolder(activeSubject, questionSet),
            rearrangeable,
            isSelected: this.isQuestionSetSelected,
            selectedQuestionSets: this.state.selectedQuestionSets,
            questionSetOnChangeHandler: this.makeQuestionSetOnChangeHandler,
            isSelectAllQuestionSetsMode: this.state.isSelectAllQuestionSetsMode,
            archivedCheckboxTooltip: isArchivedQuestionView ? ARCHIVED_DISABLED_TOOLTIP : undefined
          }}
          questionProps={{
            showAttemptIndicators,
            strikethroughQuestionInfo,
            restrictionGuideLevelName,
            restrictionGuideLevelHref,
            restrictionGuideLevelId,
            restrictionGuideLevelIds: isRestrictionAdmin
              ? ({questionSet}) => getQuestionSetGuideLevelIds(questionSet)
              : null,
            questionPath: ({questionSet, question}) =>
              getPathToQuestionSetAndQuestion({
                subjectSlug,
                guideLevelSlug,
                questionSet,
                question
              }),
            attemptIndicatorValue: ({question}) => {
              return (
                !question.getMeta().isBlockedAssignmentQuestion() &&
                this.state.guessResults.getGuessResult(question.getId())
              );
            },
            // @todo: Implement this once folder metadata exists
            isBookmarked: () => false,
            onQuestionClick: ({question, questionSet}) => {
              return (e) => {
                const isPlainClick =
                  e && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
                if (
                  isPlainClick &&
                  this.unsubmittedResponseGuard.current?.hasUnsubmittedResponse()
                ) {
                  e.preventDefault();
                  this.confirmLeavingActiveQuestion(question).then((proceed) => {
                    if (proceed) {
                      this.navigateToQuestion(questionSet, question);
                    }
                  });
                  return;
                }
                this.setState({
                  activeQuestion: question,
                  activeQuestionSet: questionSet,
                  activeGuess: null
                });
              };
            }
          }}
          questionRendererProps={{
            actions: this.getFooterActions(),
            rendererType: activeGuess ? 'PostGuess' : 'PreGuess',
            navigateBackProps,
            navigateNextProps,
            headingActions: this.getHeadingActions()
          }}
          stageComponent={this.getStageComponent()}
          partialCreditEnabled={partialCreditEnabled}
          className={CALCULATOR_DOCK_MARGIN_CLASS}
        />
        <PracticeCalculator
          tier={calculatorTier}
          resetKey={activeQuestion?.getId()}
          pageMinWidth={PRACTICE_VIEW_MIN_WIDTH}
        />
        <UnsubmittedResponseGuard
          ref={this.unsubmittedResponseGuard}
          question={activeQuestion}
          hasActiveGuess={!!activeGuess}
          getTimeElapsed={() => this.timeElapsedManager.getTimeElapsed(activeQuestion)}
          onSubmitted={this.handleGuessSubmission}
        />
        {this.state.showFolderModal && (
          <FoldersModal
            selectedQuestionSetIds={
              this.state.openedModalFromHeading
                ? [activeQuestionSet.getId()]
                : [...this.state.selectedQuestionSets]
            }
            questionSetsQuery={questionSetsQuery}
            closeModalFunc={this.toggleFoldersModal}
            isSelectAllQuestionSetsMode={isSelectAllQuestionSetsMode}
            selectedQuestionCount={this.getSelectedQuestionCount()}
          />
        )}
        {this.state.showRetailAssessmentModal && (
          <RetailAssessmentModal handleClose={this.toggleRetailAssessmentModal} />
        )}
      </MobilePracticeViewProvider>
    );
  }
}

const getCurrentGuideLevel = async (subjectId, guideLevelSlug, questionSlug) => {
  // attempt to get guide level from slug
  const subjectGuideLevel = await getSubjectGuideLevelBySlug(subjectId, guideLevelSlug);
  if (subjectGuideLevel) {
    return subjectGuideLevel;
  }

  // get direct parent guide level from single question
  const guideLevelResources = await getQuestionGuideLevels(questionSlug);
  const guideLevels = guideLevelResources.getCollection().toArray();
  if (guideLevels.length === 0) {
    // The question's guide levels may all be unpublished (e.g. moved to an
    // unpublished container). Fall back to the subject so the practice view
    // can still initialize from a direct question link.
    return null;
  }
  const parentLevel = Math.max(...guideLevels.map((guideLevel) => guideLevel.getNlevel()));
  return guideLevels.find((guideLevel) => guideLevel.getNlevel() === parentLevel) || null;
};

/** Resets above the question, which changes without remounting. */
const AnnotatableSubjectPracticeView = (props) => {
  const supportsAnnotations = useIsSubjectPracticeAnnotationAvailable();
  useAnnotationModeReset();

  return <BootstrappedSubjectPracticeView {...props} supportsAnnotations={supportsAnnotations} />;
};

class SubjectPracticeView extends React.Component {
  static propTypes = {
    params: PropTypes.object,
    location: PropTypes.object,
    router: PropTypes.object,
    route: PropTypes.object,
    subject: PropTypes.instanceOf(getModelForResourceType('subject_v2')),
    questionSets: PropTypes.instanceOf(List),
    classrooms: PropTypes.instanceOf(List)
  };

  constructor(props) {
    super(props);

    const showPublicOnlyView = showPublicViewForSubject(props.subject);

    let questionSetsQuery = null;
    let questionSets = null;
    let isReady = false;
    let isLoading = true;

    if (props.questionSets) {
      questionSetsQuery = getSingleQuestionSetQuery(props);
      questionSets = props.questionSets;
      isReady = true;
      isLoading = false;
    }

    this.state = {
      questionSets,
      questionSetsQuery,
      isReady,
      isLoading,
      guideLevel: null,
      subject: props.subject,
      blockedQuestionCount: 0,
      showBlockedAssignmentQuestionModal: false,
      queryDiffString: JSON.stringify(props.location.query),
      errorResponse: null,
      showUpgradeModal: true,
      prevLocation: '',
      userClassrooms: List(),
      showPublicOnlyView,
      guesses: List()
    };
  }

  didRedirectToError = false;

  didRedirectNoSubjectAccess = false;

  componentDidMount() {
    this.redirectIfBlocked();
    if (!this.props.questionSets) {
      this.setInitialState().then(this.checkForBlockedQuestions).catch(this.handleInitError);
    }
  }

  componentDidUpdate() {
    this.redirectIfBlocked();
    const latestQueryDiffString = JSON.stringify(this.props.location.query);
    if (this.state.queryDiffString !== latestQueryDiffString) {
      this.setState(
        {
          queryDiffString: latestQueryDiffString,
          isLoading: true
        },
        () => {
          this.setInitialState().catch(this.handleInitError);
        }
      );
    }
  }

  static queryTypes = queryTypes;

  isAccessBlocked() {
    const {errorResponse} = this.state;
    return Boolean(errorResponse?.forbidden || (!sessionStore.hasValidSession() && errorResponse));
  }

  isLicensedStudentWithoutSubjectAccess() {
    const {userClassrooms, subject} = this.state;
    return (
      sessionStore.hasValidSession() &&
      sessionStore.isStudent() &&
      !userClassrooms.isEmpty() &&
      !subject.doesUserHaveAccess()
    );
  }

  // Router transitions are async, so navigate at most once per instance.
  redirectIfBlocked() {
    const {errorResponse, subject} = this.state;

    if (this.isAccessBlocked()) {
      return;
    }

    if (errorResponse && !this.didRedirectToError) {
      this.didRedirectToError = true;
      if (history.getCurrentLocation().pathname !== '/error') {
        this.props.router.pushState(null, '/error');
      }
      return;
    }

    if (this.isLicensedStudentWithoutSubjectAccess() && !this.didRedirectNoSubjectAccess) {
      this.didRedirectNoSubjectAccess = true;
      const subjectPath = `/${subject.getUrlSlug()}`;
      if (history.getCurrentLocation().pathname !== subjectPath) {
        history.replace(subjectPath);
      }
    }
  }

  setInitialState = async () => {
    const userId = masqueradeStore.getUserIdByMasqueradeState();
    /* There is asynchronous behavior downstream in getQuestionSetsData we must resolve first */
    const userClassrooms = await userLicensedSubjectsQuery(true).getResourcePromise();

    const guideLevel = await getCurrentGuideLevel(
      this.props.subject.getId(),
      this.props.params.guideLevelSlug || '',
      this.props.params.questionSlug || ''
    );

    /*
     * Users without access to the subject get the read-only PublicQuestions view for
     * non-free guide levels, so skip the question sets query. The guesses query must
     * also be skipped: it includes question content the user is not authorized to read,
     * so it 403s for any student with prior guesses in the guide level (e.g. a student
     * whose classroom was switched from Licensed to Unlicensed).
     */
    const isPublicPreview = !guideLevel?.isFree() && this.state.showPublicOnlyView;

    const questionSetsData = await getQuestionSetsData(
      this.props,
      userId,
      guideLevel?.getId(),
      isPublicPreview
    );

    let guesses = List();

    if (sessionStore.hasValidSession() && guideLevel && !isPublicPreview) {
      guesses = await studyGuideUtils
        .getGuessesByUserIdAndGuideLevelIdQuery(
          sessionStore.getUserId(),
          guideLevel.getId(),
          sessionStore.isStudent()
        )
        .getResourcePromise();
    }

    const {questionSetsQuery, questionSets, blockedQuestionCount, errorResponse} = questionSetsData;
    const prevLocation = this.props.location?.state?.from;
    this.setState({
      questionSetsQuery,
      questionSets,
      guideLevel,
      blockedQuestionCount,
      isReady: true,
      isLoading: false,
      errorResponse,
      prevLocation,
      userClassrooms,
      guesses
    });
  };

  checkForBlockedQuestions = () => {
    if (this.state.blockedQuestionCount > 0) {
      this.setState({showBlockedAssignmentQuestionModal: true});
    }
  };

  // Stale `/learn` slugs (overwhelmingly web crawlers hitting removed content) make
  // the guide-level lookup in setInitialState reject with a superagent 404. Route those
  // to the standard error page instead of leaking an unhandled promise rejection.
  // See Honeybadger fault 128962314.
  handleInitError = (error) => {
    if (error?.status === 404) {
      this.props.router.pushState(null, '/error');
      return;
    }
    // Any other init failure (including the latent case where getCurrentGuideLevel
    // resolves to undefined and a downstream getId() throws) leaves us without a usable
    // guideLevel, which render() dereferences unconditionally. Surface a truthy
    // errorResponse so the existing render branch redirects to '/error' rather than
    // crashing render on a null guideLevel.
    this.setState({
      errorResponse: error?.response ?? {message: 'Failed to load practice view'},
      isReady: true,
      isLoading: false
    });
  };

  dismissModal = () => {
    this.setState({
      showBlockedAssignmentQuestionModal: false
    });
  };

  toggleUpgradeModal = () => {
    this.setState({
      showUpgradeModal: false
    });
  };

  render() {
    const {
      isReady,
      isLoading,
      questionSetsQuery,
      blockedQuestionCount,
      questionSets,
      guideLevel,
      subject,
      showBlockedAssignmentQuestionModal,
      errorResponse,
      showUpgradeModal,
      prevLocation,
      userClassrooms,
      showPublicOnlyView,
      guesses
    } = this.state;
    const {classrooms, params, route} = this.props;

    const isPartialCreditEnabledForSubject = studyGuideUtils.isPartialCreditEnabledForSubject(
      subject,
      classrooms,
      sessionStore.isStudent()
    );

    const subjectLink = subject.isAdaptive()
      ? `/adaptive-subjects/${params.subjectSlug}`
      : `/${params.subjectSlug}`;

    if (!isReady) {
      return <LoadingView />;
    }
    if (this.isAccessBlocked()) {
      return (
        <>
          <PracticeView
            titleBar={
              <TitleBar
                backButton={
                  <TitleBar.BackButton as={Link} label='Navigate to subject' to='/subjects' />
                }
                title='To course library'
              />
            }
            questionsListProps={{
              header: <QuestionsListHeader />,
              noQuestionSetsMessage: 'You do not have access to these question(s).'
            }}
          />
          {sessionStore.hasValidSession()
            ? showUpgradeModal && (
                <UpgradeSubjectModal handleClose={this.toggleUpgradeModal} subject={subject} />
              )
            : showGlobalLoginModal()}
        </>
      );
    }
    if (errorResponse || this.isLicensedStudentWithoutSubjectAccess()) {
      return <LoadingView />;
    }

    if (!sessionStore.hasValidSession() && guideLevel.getGuide().getGuideType() !== 'practice') {
      showGlobalLoginModal();
      return null;
    }

    // if the user is not licensed for the current subject, or the selected guide
    // level is not free, block all question data except title in a
    // read-only view. PracticeView should ultimately be updated for more flexibility
    // around displaying question content, but this shim exists for now to block
    // content for unlicensed users
    if (showPublicOnlyView && !guideLevel?.isFree()) {
      const title = guideLevel ? guideLevel.getName() : subject.getName();
      const curriculumAreaSlug = subject.getCurriculumAreaSlug();
      const subjectColor = `var(--color-background-domain-${curriculumAreaSlug})`;
      return (
        <PublicQuestions
          backLocation={prevLocation || addSearchParamsToLink(subjectLink)}
          guideLevelSlug={params.guideLevelSlug}
          iconUrl={subject.getLogoLocation().href}
          onForbidden={({response}) => this.setState({errorResponse: response})}
          queryType={route.queryType}
          questionSetSlug={params.questionSetSlug}
          questionSlug={params.questionSlug}
          router={this.props.router}
          subjectColor={subjectColor}
          subjectSlug={params.subjectSlug}
          title={title}
        />
      );
    }

    const isAssessment = guideLevel?.getGuide().getGuideType() === 'assessment';
    const retailStudentInAssessment =
      sessionStore.isStudent() && userClassrooms.isEmpty() && isAssessment;
    const questionSetMeta = questionSetsQuery.getResourceMetadata();
    const questionCount = questionSetMeta.getIn(['page', 'total_entries'], 0);

    return (
      <div>
        <AnnotatableSubjectPracticeView
          {...this.props}
          isLoading={isLoading}
          questionSetsQuery={questionSetsQuery}
          questionSetsCount={questionCount}
          questionCount={questionCount}
          questionSets={questionSets}
          guideLevel={guideLevel}
          subject={subject}
          prevLocation={prevLocation}
          retailStudentInAssessment={retailStudentInAssessment}
          partialCreditEnabled={isPartialCreditEnabledForSubject}
          guesses={guesses}
        />
        {showBlockedAssignmentQuestionModal && (
          <BlockedQuestionsModal
            blockedQuestionCount={blockedQuestionCount}
            questionCount={questionCount}
            subjectSlug={params.subjectSlug}
            handleDismiss={this.dismissModal}
          />
        )}
      </div>
    );
  }
}

const getInitialQueries = (props) => {
  const userId = masqueradeStore.getUserIdByMasqueradeState();

  const queries = {
    subject: getSubjectBySlugQuery(props.params.subjectSlug, userId)
  };

  if (!props.params.guideLevelSlug && !sessionStore.hasValidSession()) {
    queries.questionSets = getSingleQuestionSetQuery(props, userId);
  }

  if (sessionStore.hasValidSession()) {
    queries.classrooms = studyGuideUtils.getClassroomsByUserIdQuery(
      sessionStore.getUserId(),
      sessionStore.isStudent()
    );
  }

  return queries;
};

export default awaitMandarkQueries((props) => {
  const queries = getInitialQueries(props);
  return {queries};
}, SubjectPracticeView);
